import { Maybe } from '@codegrade/maybe';
import { sort } from '@codegrade/utils';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import client from '@/client';
import * as utils from '@/utils';
const storeBuilder = getStoreBuilder();
/** @returns The initial state. */
function makeInitialState() {
    return {
        byCourse: {},
        studentRoleByCourse: {},
        studentRoleByCourseLoader: {},
    };
}
const moduleBuilder = storeBuilder.module('course-roles', makeInitialState());
/** The store for the courseRoles. */
export var CourseRolesStore;
(function (CourseRolesStore) {
    /** Clear the entire course roles store. */
    CourseRolesStore.commitClearAll = moduleBuilder.commit(state => Object.assign(state, makeInitialState()), 'commitClearAll');
    const commitCourseRoles = moduleBuilder.commit((state, { courseId, data }) => {
        let items = data.data;
        if (items != null) {
            items = sort.sortBy(items, courseRole => [courseRole.name]);
        }
        utils.vueSet(state.byCourse, courseId, { ...data, data: items });
    }, 'commitCourseRoles');
    const commitStudentRole = moduleBuilder.commit((state, { courseId, role }) => {
        utils.vueSet(state.studentRoleByCourse, courseId, role);
    }, 'commitStudentRole');
    const commitStudentRoleLoader = moduleBuilder.commit((state, { courseId, loader }) => {
        utils.vueSetOrDelete(state.studentRoleByCourseLoader, courseId, loader);
    }, 'commitStudentRoleLoader');
    const commitUpdateCourseCourseRoles = moduleBuilder.commit((state, { courseId, updater }) => {
        const existing = state.byCourse[courseId];
        if (existing == null) {
            return;
        }
        commitCourseRoles({
            courseId,
            data: {
                ...existing,
                data: existing.data?.map(g => updater(g)),
            },
        });
    }, 'commitUpdateCourseRole');
    /** Get the course roles of the given course from the store. */
    CourseRolesStore.getForCourse = moduleBuilder.read(state => 
    /**
     * Get the course roles of the given course.
     *
     * @param courseId The id of the course for which to get the roles.
     * @returns The loaded course roles for the given course.
     */
    (courseId) => utils.getPaginatedState(state.byCourse[courseId]), 'getForCourse');
    /** Get the student role for a course from the store. */
    CourseRolesStore.getStudentRole = moduleBuilder.read(state => 
    /**
     * Get the student role for the given course.
     *
     * @param courseId The id of the course to get the student role for.
     * @returns The student role, doubly wrapped in a `Maybe`. The inner `Maybe` indicates
     * whether a course role exists for the given course. The outer `Maybe` indicates
     * whether enough data has been loaded to be certain whether a student role exists for
     * the given course: if it is a `Just`, loading more course roles will not change the
     * result.
     */
    (courseId) => {
        const found = state.studentRoleByCourse[courseId];
        if (found != null) {
            return Maybe.of(found);
        }
        const allRoles = CourseRolesStore.getForCourse()(courseId);
        for (const role of allRoles.data) {
            if (role.name === 'Student') {
                return Maybe.of(Maybe.of(role));
            }
        }
        if (allRoles.state === 'loaded-all') {
            return Maybe.of(Maybe.empty());
        }
        return Maybe.empty();
    }, 'getStudentRole');
    /** Load the course roles of a specific course. */
    CourseRolesStore.loadForCourse = moduleBuilder.dispatch(async ({ state }, { courseId, toLoad }) => {
        await utils.loadPaginatedWithForce({
            commitData: data => commitCourseRoles({ courseId, data }),
            getData: () => state.byCourse[courseId],
            load: async (nextToken) => {
                const res = await client.p.course.getCourseRoles({ courseId, nextToken });
                const extended = await Promise.all(res.data.map(({ id }) => client.p.course.getCourseRole({ courseId, roleId: id })));
                return {
                    ...res,
                    data: extended.map(result => result.cgResult),
                };
            },
            toLoad,
        });
    }, 'loadForCourse');
    /** Load the role to be used as the student role for a course. */
    CourseRolesStore.loadStudentRole = moduleBuilder.dispatch(async ({ state }, { courseId, force = false }) => {
        await utils.storeLoadWithMaybeForce({
            addLoader: loader => commitStudentRoleLoader({ courseId, loader: Maybe.of(loader) }),
            removeLoader: () => commitStudentRoleLoader({ courseId, loader: Maybe.empty() }),
            force,
            loader: Maybe.fromNullable(state.studentRoleByCourseLoader[courseId]),
            value: CourseRolesStore.getStudentRole()(courseId),
            load: async () => {
                let studentRole;
                for await (const role of utils.loadAllPages(async (nextToken) => await client.p.course.getCourseRoles({
                    courseId,
                    q: 'Student',
                    nextToken: nextToken.extract(),
                }))) {
                    if (role.name === 'Student') {
                        studentRole = await client.p.course
                            .getCourseRole({
                            courseId,
                            roleId: role.id,
                        })
                            .then(res => res.cgResult);
                        break;
                    }
                }
                commitStudentRole({ courseId, role: Maybe.fromNullable(studentRole) });
            },
        });
    }, 'loadStudentRole');
    /** Create a new course role. */
    CourseRolesStore.createCourseRole = moduleBuilder.dispatch(async ({ state }, { courseId, data }) => {
        const res = await client.p.course.createRole({
            courseId,
            requestBody: data,
        });
        return res.addCallbacks({
            onSuccess: () => {
                const paginatedState = state.byCourse[courseId] ?? {
                    pagination: { tag: 'not-started' },
                };
                commitCourseRoles({
                    courseId,
                    data: {
                        ...paginatedState,
                        data: [res.cgResult, ...(paginatedState.data ?? [])],
                    },
                });
            },
        });
    }, 'createCourseRole');
    /** Update a course role. */
    CourseRolesStore.patchCourseRole = moduleBuilder.dispatch(async (_, { courseId, roleId, data }) => {
        const res = await client.p.course.patchRole({
            courseId,
            roleId,
            requestBody: data,
        });
        return res.addCallbacks({
            onSuccess: () => {
                commitUpdateCourseCourseRoles({
                    courseId,
                    updater: old => (old.id === roleId ? res.cgResult : old),
                });
            },
        });
    }, 'patchCourseRole');
    /** Delete a course role. */
    CourseRolesStore.deleteCourseRole = moduleBuilder.dispatch(async ({ state }, { courseId, roleId }) => {
        const res = await client.p.course.deleteRole({ courseId, roleId });
        return res.addCallbacks({
            onAfterSuccess: () => {
                const data = state.byCourse[courseId];
                if (data == null) {
                    return;
                }
                commitCourseRoles({
                    courseId,
                    data: {
                        ...data,
                        data: data.data?.filter(gs => gs.id !== roleId),
                    },
                });
            },
        });
    }, 'deleteCourseRole');
    utils.signal.AFTER_USER_CHANGED.registerSync(() => {
        CourseRolesStore.commitClearAll();
    });
})(CourseRolesStore || (CourseRolesStore = {}));
